

import type { AppStore } from './app';
import type { Score } from '../types/score';
import type { TeamRecord } from '../types/team';
import { makeAutoObservable, toJS } from 'mobx';

import { publishError, publishSentryMessage } from '../configs/sentry/sentry-utils';


export class TeamStore {
  initialData: TeamRecord[] = [];

  fetching = false;
  data = this.initialData;
  score: Score | undefined;

  constructor(private appStore: AppStore) {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get isCaptain() {
    const me = this.data.find(
      ({ user_token }) => user_token === this.appStore.gameData?.user_token,
    );
    return Boolean(me?.is_captain);
  }

  get token() {
    if (!this.appStore.token) {
      throw new Error('Token is required');
    }

    return this.appStore.token;
  }

  get activeUsers() {
    const activeUsers = this.data.filter((item) => Boolean(item.is_active));

    if (this.data.length > 0 && !activeUsers.length) {
      publishSentryMessage('Active users are empty.', {
        extra: {
          data: toJS(this.data),
        },
      });
    }

    return activeUsers;
  }

  init = () => {
    this.listenSocket();
    this.fetch();
  };

  fetch = async () => {
    try {
      this.setFetching(true);
      const { data } = await this.appStore.api.team(this.token);
      this.setData(data.team);
    } catch (error) {
      publishError(error as Error);
    } finally {
      this.setFetching(false);
    }
  };

  listenSocket = () => {
    const socket = this.appStore.socket;
    if (!socket) {
      return;
    }
    socket.on('teamStat', (data) => {
      this.setScore(data);
    });
    socket.on('updateTeam', (data) => {
      if (Array.isArray(data?.team)) {
        this.setData(data.team);
      }
    });
    socket.io.on('reconnect', this.fetch);
  };

  setData(data: TeamRecord[]) {
    this.data = data;
  }

  setScore(data: Score) {
    this.score = data;
  }

  setFetching(value: boolean) {
    this.fetching = value;
  }
}
